/**
 * AdminPage - Orchestrates admin feature sections with tab navigation
 * Manages organizations, users, agent configurations, and monitors system performance
 */

import React, { useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Alert } from '@/components/ui';
import { Toast } from '@/components/ui/Toast';
import { AuthContext } from '@/contexts/AuthContext';
import {
  useNotificationState,
  useNotification,
} from '@/stores/notificationStore';
import { MonitoringSection } from '@/features/admin/monitoring';
import { OrganizationsSection } from '@/features/admin/organizations';
import { UsersSection } from '@/features/admin/users';
import { AgentInstructionsSection } from '@/features/admin/agent-instructions';
import { BillingSection } from '@/features/admin/billing';
import { FeedbackSection } from '@/features/admin/feedback';
import useDocumentTitle from '@/hooks/useDocumentTitle';

// Admin Panel Icon
const AdminPanelIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className || 'w-8 h-8'}
  >
    <path
      fillRule="evenodd"
      d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.75.75 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08zm3.094 8.016a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
      clipRule="evenodd"
    />
  </svg>
);

// Tab configuration
const ADMIN_TABS = [
  { key: 'monitoring', label: 'Monitoring' },
  { key: 'feedback', label: 'Feedback' },
  { key: 'organizations', label: 'Organizations' },
  { key: 'users', label: 'Users' },
  { key: 'agent-instructions', label: 'Agent Instructions' },
  { key: 'billing', label: 'Billing' },
] as const;

type AdminTab = (typeof ADMIN_TABS)[number]['key'];

const AdminPage: React.FC = () => {
  // Set document title
  useDocumentTitle('Admin');

  const authContext = useContext(AuthContext);
  const notificationState = useNotificationState();
  const { closeNotification } = useNotification();
  const [activeTab, setActiveTab] = useState<AdminTab>('monitoring');

  // Auth check
  if (!authContext?.currentUser) {
    return <Navigate to="/login" replace />;
  }

  const userRole = authContext.systemRole || 'USER';
  const isAdminUser = authContext.isAdmin;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'monitoring':
        return <MonitoringSection />;
      case 'feedback':
        return <FeedbackSection />;
      case 'organizations':
        return <OrganizationsSection />;
      case 'users':
        return <UsersSection />;
      case 'agent-instructions':
        return <AgentInstructionsSection />;
      case 'billing':
        return <BillingSection />;
      default:
        return <MonitoringSection />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center mb-6">
        <AdminPanelIcon className="w-8 h-8 mr-3 text-primary-600" />
        <h1 className="text-3xl font-bold text-secondary-900">
          Admin Dashboard
        </h1>
      </div>

      {/* Admin Role Status */}
      <Alert variant={isAdminUser ? 'success' : 'warning'} className="mb-6">
        <p className="text-sm">
          <strong>Access Status:</strong>{' '}
          {isAdminUser ? 'Admin Access Granted' : 'Limited Access'}
          &nbsp;|&nbsp;
          <strong>Role:</strong> {userRole}
          &nbsp;|&nbsp;
          <strong>User:</strong> {authContext.currentUser?.email}
        </p>
        {!isAdminUser && (
          <p className="text-sm mt-1">
            Note: Some admin features may be restricted. Contact an
            administrator to request ADMIN role assignment.
          </p>
        )}
      </Alert>

      <p className="text-secondary-600 mb-6">
        Manage organizations, users, agent configurations, and monitor system
        performance.
      </p>

      {/* Tab Navigation */}
      <div className="border-b border-secondary-200 mb-8">
        <nav className="flex gap-1 -mb-px overflow-x-auto">
          {ADMIN_TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors
                  ${
                    isActive
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                  }
                `}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-8">{renderTabContent()}</div>

      {/* Global Toast Notification */}
      <Toast
        open={notificationState.open}
        message={notificationState.message}
        variant={notificationState.severity}
        onClose={closeNotification}
      />
    </div>
  );
};

export default AdminPage;
